import { consoleLogger } from './console-logger'
import { LogLevel, SDKLogger } from './types'

/** Derive an SDK-scoped logger from user options with safe fallbacks. */
export function deriveSdkLogger(
  options: {
    logger?: any
    logLevel?: LogLevel
  } = {},
  bindings: Record<string, unknown> = {},
): SDKLogger {
  const { logger, logLevel = 'error' } = options

  // 1️⃣ No user logger → use console fallback
  if (!logger) {
    return consoleLogger(logLevel, bindings)
  }

  // 2️⃣ Scope safely (do NOT mutate root logger)
  let scoped: any

  if (typeof logger.child === 'function') {
    scoped = logger.child(bindings)

    // Try to enforce SDK logLevel on child only (pino-style)
    if (logLevel !== 'silent' && 'level' in scoped) {
      try {
        scoped.level = logLevel
      } catch {
        // ignore if readonly
      }
    }

    return scoped as SDKLogger
  }

  // 3️⃣ No child() support → wrap
  return wrapLogger(logger, bindings)
}

/**
 * Wraps a logger that does NOT support .child()
 * by injecting bindings into every call.
 */
function wrapLogger(base: any, bindings: Record<string, unknown>): SDKLogger {
  const wrap =
    (method: string) =>
    (...args: any[]) => {
      if (typeof base[method] !== 'function') return

      // Append structured bindings as final argument
      base[method](...args, bindings)
    }

  return {
    trace: wrap('trace'),
    debug: wrap('debug'),
    info: wrap('info'),
    warn: wrap('warn'),
    error: wrap('error'),
  }
}
